import React, { useState, useEffect } from 'react';
import { useProjectStore, usePoleStore } from '../../stores';
import { Pole } from '../../types';
import { PolePhotosTab } from './PolePhotosTab';
import { CassetteEditorDialog } from './CassetteEditorDialog';
import { getErrorMessage } from '../../utils/errorMessages';
import './Dialog.css';
import './EditPoleDialog.css';

interface EditPoleDialogProps {
  isOpen: boolean;
  onClose: () => void;
  pole: Pole;
  initialTab?: TabType;
}

type TabType = 'basic' | 'edit' | 'photos' | 'cassette';

export const EditPoleDialog: React.FC<EditPoleDialogProps> = ({ isOpen, onClose, pole, initialTab = 'basic' }) => {
  const { currentProject } = useProjectStore();
  const { updatePole, updatePoleLocal, fetchPoles } = usePoleStore();

  const [activeTab, setActiveTab] = useState<TabType>(initialTab);
  const [name, setName] = useState(pole.name);
  const [poleType, setPoleType] = useState(pole.pole_type);
  const [oltPortCount, setOltPortCount] = useState(pole.olt_port_count || 1);
  const [expectedCustomers, setExpectedCustomers] = useState(pole.expected_customers || 0);
  const [mapNote, setMapNote] = useState(pole.map_note || '');
  const [showMapNote, setShowMapNote] = useState(pole.show_map_note || false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [localPole, setLocalPole] = useState<Pole>(pole);

  useEffect(() => {
    console.log('🔄 [EditPoleDialog] Pole prop changed:', {
      poleId: pole.id,
      poleName: pole.name,
      photosCount: pole.photos?.length || 0,
      photoIds: pole.photos?.map(p => p.id) || []
    });
    setName(pole.name);
    setPoleType(pole.pole_type);
    setOltPortCount(pole.olt_port_count || 1);
    setExpectedCustomers(pole.expected_customers || 0);
    setMapNote(pole.map_note || '');
    setShowMapNote(pole.show_map_note || false);
    setLocalPole(pole);
    setActiveTab(initialTab); // Set to initialTab when pole changes
  }, [pole, initialTab]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (!currentProject) {
        throw new Error('Nie je vybraný projekt');
      }

      if (!name.trim()) {
        throw new Error('Názov je povinný');
      }

      await updatePole(currentProject.id, pole.id, {
        name: name.trim(),
        pole_type: poleType,
        olt_port_count: poleType === 'olt' ? oltPortCount : undefined,
        expected_customers: expectedCustomers,
        map_note: mapNote.trim() || undefined,
        show_map_note: showMapNote,
      });

      await fetchPoles(currentProject.id);
      onClose();
    } catch (err: any) {
      setError(getErrorMessage(err, 'Nepodarilo sa aktualizovať stĺpik.'));
    } finally {
      setLoading(false);
    }
  };

  const handlePoleUpdate = (updatedPole: Pole) => {
    console.log('⬆️ [EditPoleDialog] handlePoleUpdate called:', {
      poleId: updatedPole.id,
      photosCount: updatedPole.photos?.length || 0,
      photoIds: updatedPole.photos?.map(p => p.id) || []
    });
    setLocalPole(updatedPole);
    // Update pole in store so it persists when dialog is reopened
    updatePoleLocal(updatedPole);
  };

  // Refresh poles when dialog closes (to get updated data for next open)
  useEffect(() => {
    if (!isOpen && currentProject) {
      console.log('🔄 [EditPoleDialog] Dialog closed, fetching poles...');
      fetchPoles(currentProject.id);
    }
  }, [isOpen, currentProject, fetchPoles]);

  if (!isOpen) return null;

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog edit-pole-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>✏️ Upraviť stĺpik: {pole.name}</h2>
          <button className="dialog-close" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'basic' ? 'active' : ''}`}
            onClick={() => setActiveTab('basic')}
            type="button"
          >
            📋 Základné
          </button>
          <button
            className={`tab-button ${activeTab === 'edit' ? 'active' : ''}`}
            onClick={() => setActiveTab('edit')}
            type="button"
          >
            ✏️ Upraviť
          </button>
          <button
            className={`tab-button ${activeTab === 'photos' ? 'active' : ''}`}
            onClick={() => setActiveTab('photos')}
            type="button"
          >
            📸 Fotky ({localPole.photos?.length || 0})
          </button>
          <button
            className={`tab-button ${activeTab === 'cassette' ? 'active' : ''}`}
            onClick={() => setActiveTab('cassette')}
            type="button"
          >
            🔧 Editor kazety
          </button>
        </div>

        <div className="dialog-content">
          {/* Basic Info Tab */}
          {activeTab === 'basic' && (
            <div className="tab-content">
              <h3>📋 Základné informácie</h3>

              <div className="info-grid">
                <div className="info-item">
                  <label>Názov:</label>
                  <span>{pole.name}</span>
                </div>

                <div className="info-item">
                  <label>Typ:</label>
                  <span>
                    {pole.pole_type === 'olt' && '🔌 OLT (Optical Line Terminal)'}
                    {pole.pole_type === 'pole' && '📍 Stĺpik'}
                    {pole.pole_type === 'manhole' && '🕳️ Šachta'}
                    {pole.pole_type === 'splitter_point' && '🔀 Bod rozdelenia'}
                    {pole.pole_type === 'building' && '🏢 Budova'}
                  </span>
                </div>

                {pole.pole_type === 'olt' && (
                  <div className="info-item">
                    <label>Počet OLT portov:</label>
                    <span>{pole.olt_port_count || 1}</span>
                  </div>
                )}

                <div className="info-item">
                  <label>Očakávaný počet zákazníkov:</label>
                  <span>{pole.expected_customers || 0}</span>
                </div>

                <div className="info-item">
                  <label>GPS súradnice:</label>
                  <span>
                    {Number(pole.latitude).toFixed(6)}, {Number(pole.longitude).toFixed(6)}
                  </span>
                </div>

                {pole.map_note && (
                  <div className="info-item full-width">
                    <label>Poznámka na mape:</label>
                    <span>{pole.map_note}</span>
                  </div>
                )}

                <div className="info-item">
                  <label>Vytvorené:</label>
                  <span>{new Date(pole.created_at).toLocaleString('sk-SK')}</span>
                </div>
              </div>
            </div>
          )}

          {/* Edit Tab */}
          {activeTab === 'edit' && (
            <form onSubmit={handleSubmit} className="tab-content">
              <h3>✏️ Upraviť stĺpik</h3>

              {error && <div className="error-message">{error}</div>}

              <div className="form-group">
            <label htmlFor="name">Názov *</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="napr. OLT-Centrála"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="poleType">Typ *</label>
            <select
              id="poleType"
              value={poleType}
              onChange={(e) => setPoleType(e.target.value as any)}
              required
            >
              <option value="olt">OLT (Optical Line Terminal)</option>
              <option value="pole">Stĺpik</option>
              <option value="pole">Šachta</option>
              <option value="splitter_point">Bod rozdelenia</option>
              <option value="building">Budova</option>
            </select>
          </div>

          {poleType === 'olt' && (
            <div className="form-group">
              <label htmlFor="oltPortCount">Počet OLT portov *</label>
              <input
                id="oltPortCount"
                type="number"
                min="1"
                max="64"
                value={oltPortCount}
                onChange={(e) => setOltPortCount(parseInt(e.target.value))}
                required
              />
              <small>Počet výstupných portov (1-64)</small>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="expectedCustomers">Očakávaný počet zákazníkov</label>
            <input
              id="expectedCustomers"
              type="number"
              min="0"
              value={expectedCustomers}
              onChange={(e) => setExpectedCustomers(parseInt(e.target.value) || 0)}
            />
            <small>Očakávaný počet zákazníkov pre návrh siete (použité pre auto-návrh)</small>
          </div>

          <div className="form-group">
            <label htmlFor="mapNote">Poznámka na mape</label>
            <textarea
              id="mapNote"
              value={mapNote}
              onChange={(e) => setMapNote(e.target.value)}
              placeholder="Poznámka, ktorá sa zobrazí na mape..."
              rows={3}
              style={{ resize: 'vertical' }}
            />
            <small>Poznámka, ktorá sa zobrazí pri stĺpiku na mape</small>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={showMapNote}
                onChange={(e) => setShowMapNote(e.target.checked)}
              />
              <span>📍 Zobraziť poznámku na mape</span>
            </label>
          </div>

          <div className="form-group">
            <label>Poloha</label>
            <div className="location-display">
              <span>Lat: {Number(pole.latitude).toFixed(6)}</span>
              <span>Lng: {Number(pole.longitude).toFixed(6)}</span>
            </div>
            <small>Použite "Presunúť" z kontextového menu pre zmenu polohy</small>
          </div>

              <div className="dialog-actions">
                <button type="button" onClick={onClose} className="btn-secondary">
                  Zrušiť
                </button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Ukladá sa...' : 'Uložiť zmeny'}
                </button>
              </div>
            </form>
          )}

          {/* Photos Tab */}
          {activeTab === 'photos' && currentProject && (
            <div className="tab-content photos-tab-content">
              <PolePhotosTab
                pole={localPole}
                projectId={currentProject.id}
                onUpdate={handlePoleUpdate}
              />
            </div>
          )}

          {/* Cassette Editor Tab */}
          {activeTab === 'cassette' && (
            <div className="tab-content cassette-tab-content">
              <div className="cassette-info">
                <p>
                  Editor kazety umožňuje konfigurovať priechody, zvary a splittre pre tento stĺpik.
                </p>
                <button
                  className="btn-primary"
                  onClick={() => {
                    // Close this dialog and open cassette editor
                    // We'll use a simple approach - just show info for now
                    alert('Použite tlačidlo "🔧 Editor kazety" z kontextového menu stĺpika na mape.');
                  }}
                >
                  🔧 Otvoriť Editor kazety
                </button>
                <small style={{ display: 'block', marginTop: '10px', color: '#7f8c8d' }}>
                  Tip: Kliknite pravým tlačidlom na stĺpik na mape a vyberte "🔧 Editor kazety"
                </small>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

